/**
 * OrbitPrint — /plans (slice 4). The public plan table (GET /plans, no auth) with the signed-in
 * org's plan lit. Honest about how to upgrade today: self-serve payment is Phase 6, so Premium
 * says "ask us", Enterprise says "contact us" (owner: sold by hand until its features are real).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orbitApi, useOrbitAuth, useOrbitPath } from '../OrbitAuthContext';
import { usePlan, PLAN_LABEL } from '../plan';
import { OrbitWordmark } from '../components/OrbitShell';
import '../orbit.css';

const inf = '∞';
const peso = (n) => (n ? `₱${Number(n).toLocaleString('en-PH')}` : '₱0');

const ROWS = [
  ['Monthly', (p) => peso(p.monthly)],
  ['Yearly (10 months for 12)', (p) => (p.yearly ? peso(p.yearly) : '—')],
  ['Locations (HUBs)', (p) => (p.locations == null ? inf : String(p.locations))],
  ['Members', (p) => (p.members == null ? (p.included_members ? `${p.included_members} included, then ₱${p.extra_member} each` : inf) : String(p.members))],
  ['Printers', (p) => (p.printers == null ? inf : String(p.printers))],
  ['Documents a month', (p) => (p.documents == null ? 'Unlimited' : String(p.documents))],
  ['Pages', () => 'Unlimited'],
  ['History', (p) => (p.history_days == null ? 'Unlimited' : `${p.history_days} days`)],
];

export default function OrbitPlansPage() {
  const { user, org } = useOrbitAuth();
  const { plan } = usePlan();
  const p = useOrbitPath();
  const [table, setTable] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { orbitApi.get('/plans').then((r) => setTable(r.data)).catch(() => setErr(true)); }, []);
  const current = plan?.plan || org?.plan;
  const plans = table?.plans || [];

  return (
    <div className="orbit-root min-h-screen">
      <header className="orbit-topbar">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <OrbitWordmark small />
          <Link to={p(user ? '/' : '/login')} className="orbit-link is-muted text-sm" data-testid="orbit-plans-back">{user ? 'Back to the dashboard' : 'Sign in'}</Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-9">
        <p className="orbit-label orbit-boot orbit-d1 m-0">Plans</p>
        <h1 className="orbit-display orbit-boot orbit-d2 mt-1" style={{ fontSize: 'clamp(36px, 5vw, 56px)' }} data-testid="orbit-plans-title">
          Pay by the document.<br /><span className="orbit-grad-text">Never by the page.</span>
        </h1>
        <p className="orbit-ink2 orbit-boot orbit-d3 text-[17px] leading-relaxed max-w-2xl mt-4">
          Free is a real plan for a household. Premium is one price for a business with up to five branches. Enterprise is for chains and comes with roles per branch, reports and an API.
          {current && <> You are on <strong style={{ color: 'var(--ob-ink)' }}>{PLAN_LABEL[current] || current}</strong>{org?.name ? <> for {org.name}</> : null}.</>}
        </p>

        {err ? <p className="mt-8 orbit-muted">Could not load the plan table right now.</p> : !table ? <p className="mt-8 orbit-mono text-sm orbit-muted">Loading…</p> : (
          <>
            <div className="mt-8 grid gap-5 md:grid-cols-3 orbit-boot orbit-d4">
              {plans.map((pl) => {
                const mine = current === pl.id;
                return (
                  <section key={pl.id} className={`orbit-card p-6 ${mine ? 'is-kit' : ''}`} data-testid={`orbit-plan-card-${pl.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="orbit-display m-0" style={{ fontSize: 28 }}>{pl.label}</h2>
                      {mine && <span className="orbit-lamp is-ready"><i />Your plan</span>}
                    </div>
                    <p className="orbit-display mt-3 m-0" style={{ fontSize: 40, lineHeight: 1 }}>{peso(pl.monthly)}<span className="orbit-mono text-sm orbit-muted"> / month</span></p>
                    {pl.yearly ? <p className="orbit-mono text-xs orbit-muted mt-1 m-0">{peso(pl.yearly)} a year · 10 months for 12</p> : <p className="orbit-mono text-xs orbit-muted mt-1 m-0">No card needed</p>}
                    <ul className="list-none p-0 m-0 mt-5 grid gap-2 text-sm orbit-ink2">
                      {ROWS.slice(2).map(([label, f]) => (
                        <li key={label} className="flex justify-between gap-3" style={{ borderBottom: '1px solid var(--ob-rule)', paddingBottom: 6 }}>
                          <span className="orbit-muted">{label}</span><span style={{ color: 'var(--ob-ink)' }}>{f(pl)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5">
                      {pl.id === 'free' && <p className="text-sm orbit-muted m-0">Presets, the review queue and live status are all included.</p>}
                      {pl.id === 'premium' && (
                        <>
                          <p className="text-sm orbit-ink2 m-0">Extra locations: ₱{table.slot_price || 499} each, paid once, up to 10 locations.</p>
                          <p className="text-sm orbit-muted mt-2 m-0">Paying online arrives with the next release. To upgrade today, ask us and we switch you on the same day.</p>
                        </>
                      )}
                      {pl.id === 'enterprise' && <p className="text-sm orbit-muted m-0">For chains. Roles per branch, reports and exports, the API. Contact us and we set it up with you.</p>}
                    </div>
                  </section>
                );
              })}
            </div>
            <p className="orbit-muted text-sm mt-6 max-w-2xl">
              If a paid plan lapses, everything keeps working for {table.grace_days} more days, then the organization moves to Free. Nothing is deleted: extra locations and members are paused, and come back the moment you renew.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
